import { COMPANY_NAME, PRODUCT_NAME, SUPPORT_EMAIL } from "./legal/company.js";

/**
 * The site's marketing copy, as approved.
 *
 * Every string here comes from `docs/launch/arrowchess-com-site-2026-09.md` §3
 * — the owner-approved page design of 2026-09-01 — and is pinned by
 * `src/test/site-copy.test.ts`. It is a constant module rather than markup
 * because the same words appear on the hub and on `/about`, and because the
 * page's readers are an Apple enrollment agent, a D&B analyst and an NVIDIA
 * Inception screener: what it claims is a compliance question, not a design one.
 *
 * Two rules travel with the copy:
 *  - **Shipped features only.** Push notifications, the cross-platform rating
 *    gym and the position explorer are in progress and stay off the page.
 *  - **No date promise** about the App Store.
 *
 * **Revision 2026-09-25 — awaiting owner sign-off.** The copy review of
 * 2026-09-24 asked for one hero line and one micro-line (there were five
 * competing taglines), benefit-first pillar lines in sentence case, and
 * contractions. Every pillar line below makes exactly the claim its approved
 * fragment made — nothing added, nothing dropped — and both rules above still
 * hold. Until the owner approves, the approved strings are in git history
 * (`docs/launch/arrowchess-com-site-2026-09.md` §3 is unchanged).
 *
 * **Revision 2026-10-04 — v1 scope.** This site is the App Store Support and
 * Marketing URL, so "shipped" now means "reachable in the v1 Release build"
 * (App Review 2.3/2.3.1). Dropped: insights and eval timelines, and the free
 * analysis board (Release shows the self-play board only as a read-only
 * position preview). Reworded to what v1 has: recaps → replaying finished
 * games, adaptive spaced-repetition puzzles → the daily ladder, game import →
 * position import, bots → the offline AI (no level
 * count: v1 Release offers four tiers, not five). The comment beside each line
 * says which.
 *
 * Company facts (name, number, registered office, the statutory footer) are NOT
 * repeated here — they have one definition in `src/legal/company.ts`, which the
 * footer already renders on every page.
 */

/** The hero line: the one brand line. Approved: "The whole chess world — and watching it should make you better." */
export const ROOF_LINE = "Watch the world’s best chess. Get better at your own.";

/** The App Store line. No date, ever: an unmet date is a promise broken in public. */
export const STORE_LINE = "Coming to the App Store";

/**
 * The micro-line: what the product refuses to do, which is as much of the pitch
 * as what it does. Approved: "No ads. Privacy-first, local-first." — "private by
 * default" because signed out there is no account and no usage analytics leave
 * the phone; the one exception is opt-in: following a player or tournament
 * sends a random per-install id and the follow list (plus the push token) to
 * our notification service (Privacy §3.2, §7). "Works offline" is the local-first
 * claim a reader can check (the offline AI, below).
 */
export const POSITIONING_LINE = "No ads. Private by default, local-first.";

/**
 * What the iPhone app adds to this site, for the waitlist and the announcement
 * strip (pages/waitlist.ts). Same v1 rule as the pillars (BLINDSPOTS #12): game
 * recaps are hidden in the v1 Release build, so they are not promised here.
 */
export const APP_TEASER = "Every board here, plus openings and evaluations.";

export interface Pillar {
  title: string;
  /** Shipped features, one benefit-first sentence each; the approved fragment is noted beside it. */
  items: string[];
}

export const PILLARS: readonly Pillar[] = [
  {
    title: "Watch the elite world",
    items: [
      "Every board of a live tournament at once, with real clocks and an eval bar.", // live tournaments on a multiboard with real clocks and an eval bar
      "Short, written briefs on why a game is worth following.", // written “why follow this game” briefs
      "Replay finished games, move by move.", // v1: replaces “game and tournament recaps” (game recap is hidden in v1 Release)
      "Player pages and standings.", // player pages and standings
      "Opening albums.", // opening albums
      "Search across every tournament, player and game.", // catalog-wide search
    ],
  },
  {
    title: "Grow your own game",
    items: [
      "A fresh puzzle ladder every day.", // v1: the Home daily ladder, the same for everyone (the adaptive spaced-repetition trainer is hidden in v1)
      "Drills on the opening traps worth knowing.", // opening-trap drills
      "Turn a screenshot of a board into a position on your phone.", // v1: import a position (not a game) from a screenshot
    ],
  },
  {
    title: "Play, anywhere",
    items: [
      "Play the AI offline, right on your phone.", // v1: offline games against the on-device AI; no level count (entry points have disagreed on the number of tiers, so a count goes stale)
      "Hand-crafted board and piece themes.", // hand-crafted board and piece themes
    ],
  },
];

/**
 * The heading over the pillars.
 *
 * Not in the launch doc, which wrote them straight under the hero of a
 * single-page site. This site is a live viewer, so the pillars need to say
 * plainly that they describe the APP and not the page you are reading —
 * otherwise "daily puzzles" reads as a claim about this website.
 */
export const PILLARS_HEADING = `The ${PRODUCT_NAME} app`;
export const PILLARS_EYEBROW = STORE_LINE;

/**
 * The anchor id of the support section.
 *
 * `https://arrowchess.com/#support` is the address the App Store support URL
 * will point at (launch doc §3), so this id is a published contract: renaming
 * it breaks a link inside App Store Connect, not just a link on this page.
 */
export const SUPPORT_ANCHOR = "support";
export const SUPPORT_HEADING = "Support";
export const SUPPORT_BODY = `Questions, problems or a correction? Email ${SUPPORT_EMAIL} — a person reads every message.`;

/**
 * `/about`'s opening paragraphs.
 *
 * The launch doc designed a one-page site and left an about page open, so this
 * prose is written here rather than quoted. It states only what the rest of the
 * page can be checked against: who publishes the site, what this site is (the
 * live viewer), and what the app is. The company's registered details are not
 * re-typed — the statutory footer below carries them, from one definition.
 */
export const ABOUT_INTRO: readonly string[] = [
  `${PRODUCT_NAME} is a chess app from ${COMPANY_NAME}.`,
  "This site is where you watch it live: every round of a tournament as it’s played, board by board, with clocks, evaluations and team standings worked out from the results as they come in.",
  "It isn’t the official scoreboard of any event, and we’re not affiliated with the organisers of the tournaments we follow. The games come from public broadcasts.",
];
